import { useEffect, useState, useCallback } from "react";
import { Button } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import {
  brokerApplicationsApi,
  type BrokerApplicationDto,
  type BrokerApplicationStatus,
} from "@/api/brokerApplications";
import { useDateFormatter } from "@/utils/datetime";

// External Intake & Collection v1.0 PR 9b §8.2 — staff approval queue
// for public broker applications. Gated on Broker.Applications.Approve
// (seeded onto C0 + C2 + ADMIN by PR 9a). Hidden completely from users
// without the permission.

const FILTER_OPTIONS: BrokerApplicationStatus[] = [
  "Submitted",
  "UnderReview",
  "ChangesRequested",
  "Approved",
  "Declined",
];

export function BrokerApplicationsPage() {
  const { hasPermission } = useAuth();
  const canApprove = hasPermission("Broker.Applications.Approve");

  if (!canApprove) {
    return (
      <div className="p-space-5">
        <h1 className="text-h2 font-display text-onyx m-0">Access denied</h1>
        <p className="text-small text-gray-mid mt-space-2">
          You don't have permission to review broker applications.
        </p>
      </div>
    );
  }

  return <ApprovalQueue />;
}

function ApprovalQueue() {
  const [status, setStatus] = useState<BrokerApplicationStatus>("Submitted");
  const [rows, setRows] = useState<BrokerApplicationDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<BrokerApplicationDto | null>(null);
  const { date } = useDateFormatter();

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const data = await brokerApplicationsApi.list(status);
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load applications.");
    }
  }, [status]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div className="p-space-5 max-w-5xl mx-auto">
      <h1 className="text-h2 font-display text-onyx m-0">Broker applications</h1>
      <p className="text-small text-gray-mid mt-space-2">
        Review and act on incoming broker applications.
      </p>

      <div className="mt-space-4 flex items-center gap-space-3" role="tablist" aria-label="Filter by status">
        {FILTER_OPTIONS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            data-testid={`applications-filter-${s}`}
            className={`px-space-3 py-space-1 text-small rounded-pill border ${
              s === status
                ? "bg-onyx text-white-pure border-onyx"
                : "bg-white-pure text-onyx border-border-light"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {error && <p className="text-small text-status-error mt-space-3" role="alert">{error}</p>}

      <div className="mt-space-5 grid grid-cols-1 lg:grid-cols-3 gap-space-4">
        <div className="lg:col-span-2">
          {!rows && !error && <p className="text-small text-gray-mid">Loading…</p>}
          {rows && rows.length === 0 && (
            <p className="text-small text-gray-mid" data-testid="applications-empty">
              No applications in this status.
            </p>
          )}
          {rows && rows.length > 0 && (
            <ul className="flex flex-col gap-space-2 list-none p-0">
              {rows.map((app) => (
                <li key={app.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(app)}
                    data-testid={`application-row-${app.id}`}
                    className={`w-full text-left p-space-3 border rounded-md bg-white-pure ${
                      selected?.id === app.id ? "border-gold" : "border-border-light"
                    }`}
                  >
                    <p className="text-small font-medium text-onyx m-0">{app.contactName}</p>
                    <p className="text-caption text-gray-mid m-0 mt-space-1">
                      {app.contactEmail} · submitted {date(app.submittedAt)}
                    </p>
                    {app.declineReason && (
                      <p className="text-caption text-status-error m-0 mt-space-1">
                        Declined: {app.declineReason}
                      </p>
                    )}
                    {app.status === "ChangesRequested" && app.reviewerNote && (
                      <p className="text-caption text-status-warning m-0 mt-space-1">
                        Returned: {app.reviewerNote}
                      </p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          {selected ? (
            <ApplicationReview
              application={selected}
              onActionComplete={() => {
                setSelected(null);
                void refresh();
              }}
            />
          ) : (
            <p className="text-small text-gray-mid">Select an application to review.</p>
          )}
        </div>
      </div>
    </div>
  );
}

interface ReviewProps {
  application: BrokerApplicationDto;
  onActionComplete: () => void;
}

function ApplicationReview({ application, onActionComplete }: ReviewProps) {
  const [busy, setBusy] = useState<"approve" | "decline" | "return" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState("");
  const [returnNote, setReturnNote] = useState("");
  const { dateTime } = useDateFormatter();
  const isTerminal = application.status === "Approved" || application.status === "Declined";

  async function handleApprove() {
    setError(null);
    setBusy("approve");
    try {
      await brokerApplicationsApi.approve(application.id);
      onActionComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Approval failed.");
    } finally {
      setBusy(null);
    }
  }

  async function handleDecline() {
    setError(null);
    if (!declineReason.trim()) {
      setError("Enter a reason before declining.");
      return;
    }
    setBusy("decline");
    try {
      await brokerApplicationsApi.decline(application.id, declineReason.trim());
      onActionComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Decline failed.");
    } finally {
      setBusy(null);
    }
  }

  async function handleReturn() {
    setError(null);
    if (!returnNote.trim()) {
      setError("Enter a note before returning the application.");
      return;
    }
    setBusy("return");
    try {
      await brokerApplicationsApi.return(application.id, returnNote.trim());
      onActionComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Return failed.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="p-space-4 border border-border-light rounded-md bg-white-pure" data-testid="application-review">
      <h2 className="text-h4 font-display text-onyx m-0">{application.contactName}</h2>
      <p className="text-caption text-gray-mid m-0 mt-space-1">{application.contactEmail}</p>
      <p className="text-caption text-gray-mid m-0 mt-space-1">Status: {application.status}</p>
      <p className="text-caption text-gray-mid m-0 mt-space-1">
        Submitted: {dateTime(application.submittedAt)}
      </p>
      {application.declineReason && (
        <p className="text-caption text-status-error m-0 mt-space-2">
          Decline reason: {application.declineReason}
        </p>
      )}
      {application.reviewerNote && application.status === "ChangesRequested" && (
        <p className="text-caption text-status-warning m-0 mt-space-2" data-testid="application-reviewer-note">
          Reviewer note: {application.reviewerNote}
        </p>
      )}

      {!isTerminal && (
        <div className="mt-space-4 flex flex-col gap-space-3">
          <Button
            onClick={() => void handleApprove()}
            disabled={busy !== null}
            data-testid="application-approve"
          >
            {busy === "approve" ? "Approving…" : "Approve"}
          </Button>

          <div>
            <label className="text-caption text-onyx block">Return with note (request changes)</label>
            <textarea
              value={returnNote}
              onChange={(e) => setReturnNote(e.target.value)}
              rows={3}
              disabled={busy !== null}
              data-testid="application-return-note"
              className="w-full mt-space-1 p-space-2 border border-border-light rounded-md text-small"
            />
            <Button
              variant="secondary"
              onClick={() => void handleReturn()}
              disabled={busy !== null}
              data-testid="application-return"
              className="mt-space-2"
            >
              {busy === "return" ? "Returning…" : "Return with comments"}
            </Button>
          </div>

          <div>
            <label className="text-caption text-onyx block">Decline reason</label>
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              rows={3}
              disabled={busy !== null}
              data-testid="application-decline-reason"
              className="w-full mt-space-1 p-space-2 border border-border-light rounded-md text-small"
            />
            <Button
              variant="secondary"
              onClick={() => void handleDecline()}
              disabled={busy !== null}
              data-testid="application-decline"
              className="mt-space-2"
            >
              {busy === "decline" ? "Declining…" : "Decline"}
            </Button>
          </div>

          {error && <p className="text-small text-status-error m-0" role="alert">{error}</p>}
        </div>
      )}
    </div>
  );
}
